import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Axios from 'axios';
import { FormattedMessage, injectIntl } from 'react-intl';
import classnames from 'classnames';
import { Chart, BarElement, Legend, LinearScale, Tooltip, CategoryScale } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import intlShape from '@/shared/utils/intlShape';

import LoadingAnimation from '@/shared/components/LoadingAnimation';
import api from '@/shared/api';
import config from '@/config';
import handleHttpError from '@/shared/services/HandleHttpError';
import { NoDecimalsTooltipLabel } from '@/shared/utils/chartjsHelpers';

import { injectStateService, StateService } from '@/shared/services/StateService';
import messages from '../dashboardMessages';
import { listItemMsg } from '@/shared/components/FormattedListValue';

Chart.register(Legend, LinearScale, Tooltip, CategoryScale, BarElement);
const lastIndexWithValue = array => {
  const index = array
    .slice()
    .reverse()
    .findIndex(x => !!x);
  return index >= 0 ? array.length - 1 - index : -1;
};

const getDataSetColor = datasetIndex => (datasetIndex % 2 === 0 ? '#E0DBFD' : '#7564B6');
const getLabelColors = labels => labels.map((item, i) => (i % 2 === 0 ? '#E0DBFD' : '#7564B6'));
const getMonthLabel = (intl, month) => intl.formatMessage(listItemMsg('month', month));

const PeppolDeliveryStatsChart = ({ intl, companyId, stateService, data: rawData }) => {
  const [chartData, setChartData] = useState(null);

  const buildChartData = useCallback(() => {
    if (!rawData || !rawData.invoiceDeliveryStats?.datasets) return;
    const { datasets = [], xLabels } = rawData.invoiceDeliveryStats;

    const [currentYearDataset] = datasets;
    if (!currentYearDataset) return;

    const chartXLabels = xLabels.map(x => getMonthLabel(intl, parseInt(x)));

    setChartData({
      labels: chartXLabels,
      datasets: datasets.map((dataset, idx) => ({
        label:
          idx === 0
            ? intl.formatMessage(messages.SentByEmail)
            : intl.formatMessage(messages.SentByPeppol),
        backgroundColor: getDataSetColor(idx),
        borderColor: getDataSetColor(idx),
        maxBarThickness: 50,
        data: dataset.values,
      })),
    });
  }, [intl, rawData]);

  useEffect(() => {
    buildChartData();
  }, [buildChartData]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: { display: true },
      },
      y: {
        ticks: {
          beginAtZero: true,
        },
      },
    },
    interaction: {
      mode: 'index',
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
      },
      tooltip: {
        enabled: true,
        xPadding: 10,
        yPadding: 10,
        displayColors: false,
        callbacks: {
          label: context =>
            NoDecimalsTooltipLabel(context, intl.formatMessage(messages.Invoices).toLowerCase()),
        },
      },
    },
    events: ['click', 'mousemove'], // this is needed, otherwise onHover is not fired
    onHover: (event, chartElement) => {
      // eslint-disable-next-line no-param-reassign
      event.native.target.style.cursor = chartElement[0] ? 'pointer' : 'default';
    },
  };

  return !chartData ? null : <Bar data={chartData} options={chartOptions} />;
};

PeppolDeliveryStatsChart.propTypes = {
  intl: intlShape.isRequired,
  companyId: PropTypes.string.isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
};

export default injectStateService(injectIntl(PeppolDeliveryStatsChart));
